import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Text, Box, FormErrorMessage, Flex, Image, Portal } from '@chakra-ui/core'
import { ImagePlus } from 'lucide-react'

import { StyledFormControl, StyledFormLabel } from 'components/AdminConsole/commonUI/styles'
import { InputWithIcon } from 'components/AdminConsole/commonUI/InputWithIcon'
import { FileUploadButton } from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/Widgets/Link/FIleUploadButton'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import useLocaleText from 'components/useLocaleText'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import { useMediaState } from 'hooks/useMediaState'
import strings from 'constants/strings'
import HiddenFileInput from 'components/TasksV2/UI/HiddenInut'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import FsLightbox from 'fslightbox-react'
import { RenoveItemIcon } from 'components/TasksV2/UI/RenoveItemIcon'

const validateImageUrl = (url: string) => {
  if (!url) return true

  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname.toLowerCase()

    const validExtensions = strings.SERVER_IMAGE_FORMATS.split(',')
    const hasValidExtension = validExtensions.some((ext) => pathname.endsWith(ext))

    if (!hasValidExtension) {
      return 'URL must contain a image file with the extension .jpeg, .jpg or .png'
    }

    return true
  } catch {
    return 'Enter a valid URL'
  }
}

export const UploadImageField = () => {
  // Hooks
  const { register, setValue, triggerValidation, errors } = useFormContext()
  const t = useLocaleText('pop_image', true)
  const t_placeholder = useLocaleText('pop_image_placeholder', true)
  const isRtl = useSelector(isRtlSelector)
  const [isOpenLightBox, setIsOpenLightBox] = useState(false)
  const {
    handleSingleMediaUploading,
    imageInputRef,
    uploadedImagesURL,
    setUploadedImagesURL,
    media,
    isUploadLoading,
    handleDeleteMedia,
  } = useMediaState({
    images: [],
  })

  // Event handlers
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const data = await handleSingleMediaUploading(event)
    if (!data?.uploadedFileUrl) {
      return
    }
    const newImage = data.uploadedFileUrl
    setValue('data.pop_image_url', newImage)
    triggerValidation?.('data.pop_image_url')
  }

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value
    setValue('data.pop_image_url', url)

    const validationResult = validateImageUrl(url)
    if (validationResult === true && url) {
      setUploadedImagesURL([url])
    } else if (!url) {
      setUploadedImagesURL([])
    }

    triggerValidation?.('data.pop_image_url')
  }

  const handleUploadMedia = async (files: File[]) => {
    if (!files.length) return

    const mockEvent = {
      target: {
        files: files as unknown as FileList,
      },
    } as React.ChangeEvent<HTMLInputElement>

    await handleImageUpload(mockEvent)
  }

  const handleUploadMediaClick = () => {
    imageInputRef.current?.click()
  }

  const openViewer = () => {
    setIsOpenLightBox((prev) => !prev)
  }

  const handleRemoveMedia = () => {
    handleDeleteMedia(0)
    setValue('data.pop_image_url', '')
    triggerValidation?.('data.pop_image_url')
  }

  // Dropzone handlers
  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleUploadMedia,
    accept: `${strings.POST_FILE_ACCEPT_EXTENSIONS},image/*`,
  })

  const error = errors.data?.pop_image_url

  return (
    <DropzoneWrapper onDrop={onDrop} onPaste={handlePaste}>
      <Flex
        alignItems="flex-end"
        style={{ columnGap: '10px' }}
        opacity={isUploadLoading ? 0.4 : 1}
        pointerEvents={isUploadLoading ? 'none' : 'auto'}
      >
        <StyledFormControl w="100%" isInvalid={!!error}>
          <StyledFormLabel>
            <ImagePlus size={18} strokeWidth={2.5} />
            <Text>{t}</Text>
          </StyledFormLabel>

          <Box pos="relative">
            <InputWithIcon
              name="data.pop_image_url"
              ref={register({
                validate: validateImageUrl,
              })}
              onChange={handleUrlChange}
              pr={isRtl ? '1rem' : '2.5rem'}
              pl={isRtl ? '2.5rem' : '1rem'}
              borderRadius="5px"
              border="1px solid"
              placeholder={t_placeholder}
              _focus={{ boxShadow: '0px 0px 0px 1px black' }}
            />
            <Box
              pos="absolute"
              right={isRtl ? 'unset' : '10px'}
              left={isRtl ? '10px' : 'unset'}
              top="50%"
              transform="translateY(-50%)"
            >
              <FileUploadButton onClick={handleUploadMediaClick} />
            </Box>
          </Box>

          <FormErrorMessage>{error?.message}</FormErrorMessage>
        </StyledFormControl>
        {uploadedImagesURL[0] && (
          <Box position="relative" width="50px" height="50px" borderRadius="10px" overflow="hidden" cursor="pointer">
            <Image
              src={uploadedImagesURL[0]}
              width="100%"
              height="100%"
              alt={'Uploaded'}
              objectFit="cover"
              onClick={openViewer}
            />
            <RenoveItemIcon
              aria-label="Remove File"
              onClick={handleRemoveMedia}
              pos="absolute"
              minW="unset"
              w="16px"
              h="16px"
              right={isRtl ? 'unset' : '4px'}
              left={isRtl ? '4px' : 'unset'}
            />
          </Box>
        )}
      </Flex>

      <HiddenFileInput accept="image/*" onChange={handleImageUpload} ref={imageInputRef} />

      <Portal container={document.getElementById('root')}>
        <FsLightbox
          key={JSON.stringify(media)}
          toggler={isOpenLightBox}
          sources={media}
          sourceIndex={0}
          types={['image']}
        />
      </Portal>
    </DropzoneWrapper>
  )
}
